import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { ButtonLink } from 'stile-shared/src/components/2020/common/ButtonLink';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { H2, H3, P } from 'stile-shared/src/components/2020/common/Primitives';
import { Column } from 'stile-shared/src/components/2020/layout/Column';
import { theme } from 'stile-shared/src/styles/2020/theme';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

import { NextPageBlock } from 'components/blocks/NextPageBlock';
import { ResourceAccessBlock } from 'components/common/ResourceAccessBlock';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="Stile and the NSW Syllabus Changes"
      description="Implementing the new NSW science syllabus with Stile"
      slug={props.location.pathname}
    />
  );
}

function NSWSyllabusPage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/nsw/nsw_cover.png"
            alt=""
          ></StaticImage>
        }
      />

      <TextBlock>
        <P>
          The release of the new science syllabus brings an inevitable period of change, but it
          doesn’t need to be stressful. We’ve spent hundreds of hours analysing the changes, so you
          don’t have to.
        </P>
        <P>
          As a <strong>customisable core curriculum</strong>, Stile provides everything you need to
          deliver a high-quality, syllabus-aligned science program. Download our comprehensive
          implementation guide below, featuring our meticulously crafted scope and sequence and a
          breakdown of the changes.
        </P>
        <ButtonLink url="/resources/new-nsw-syllabus/">
          Download our NSW Syllabus Implementation Guide
        </ButtonLink>
      </TextBlock>

      <TeaserBlock
        variant="dark"
        image={<StaticImage src="../../../../stile-shared/assets/images/nsw/roadshow.png" alt="" />}
      >
        <H2>
          The Sylla-<em>bus</em> is swinging by your school!
        </H2>
        <P>
          We want to help every secondary school across NSW feel ready and confident to transition
          to the new science syllabus with Stile by their side.
        </P>
        <P>
          In Term 3, the Sylla-<em>bus</em> is swinging by your school! We’re hitting the road to
          prepare you for the new science syllabus with coffee, incursions and networking (drinks on
          us!).
        </P>
        <ButtonLink url="/nsw/">Book your visit!</ButtonLink>
      </TeaserBlock>

      <TeaserBlock
        contentSide="right"
        image={
          <Link
            openInNewTab
            url="https://www.bigmarker.com/stile-education1/The-new-NSW-Syllabus-85336525faf27ba6b8151454-569abb61399f2d99ede52728?utm_bmcr_source=marketingsite"
          >
            <StaticImage
              src="../../../../stile-shared/assets/images/nsw/on_demand_implementing.png"
              alt=""
            />
          </Link>
        }
      >
        <H2>Implementing the new science syllabus with Stile</H2>
        <P>
          <strong>30 minutes | Free on-demand PL</strong>
        </P>
        <P>
          This session is for teachers looking to hear from experts on the NSW science syllabus.
        </P>
        <P>
          Listen in on a roundtable discussion with Bill Matchett, Head of Science at Macquarie
          Fields High School, and Dr Simon Crook from CrookED Science. They’ll discuss how the
          syllabus has changed, and how Stile will support you in delivering a fully aligned science
          program before the implementation deadline.
        </P>
        <ButtonLink
          openInNewTab
          url="https://www.bigmarker.com/stile-education1/The-new-NSW-Syllabus-85336525faf27ba6b8151454-569abb61399f2d99ede52728?utm_bmcr_source=marketingsite"
        >
          Watch recording
        </ButtonLink>
      </TeaserBlock>

      <TeaserBlock
        contentSide="right"
        image={
          <Link
            openInNewTab
            url="https://www.bigmarker.com/stile-education1/The-new-science-syllabus-What-s-changed?utm_bmcr_source=Landingpage"
          >
            <StaticImage
              src="../../../../stile-shared/assets/images/nsw/on_demand_whats_changed.png"
              alt=""
            />
          </Link>
        }
      >
        <H2>The new science syllabus: What’s changed?</H2>
        <P>
          <strong>30 minutes | Free on-demand PL</strong>
        </P>
        <P>
          This session is for teachers looking to deeply understand the five key changes to new NSW
          science syllabus and how you’ll be covered with Stile.
        </P>
        <P>
          Join Alex Russell, our in-house expert on the NSW Syllabus, for an informative webinar on
          the key changes to the syllabus. Alex will discuss in detail the latest updates to the
          science syllabus, and highlight how Stile will help to ensure the transition is seamless
          and you are fully equipped.
        </P>

        <ButtonLink
          openInNewTab
          url="https://www.bigmarker.com/stile-education1/The-new-science-syllabus-What-s-changed?utm_bmcr_source=Landingpage"
        >
          Watch recording
        </ButtonLink>
      </TeaserBlock>

      <TextBlock space={theme.space.mToL} variant="beige">
        <Column>
          <H2>A customisable science curriculum, ready to implement</H2>
          <P>
            Our dynamic teaching platform empowers educators like you. It contains an array of
            comprehensive tools and instructional and teacher support materials, including
            curriculum planning documents, fully editable lesson plans, teaching prompts and model
            answers.
          </P>
          <P>
            Specifically designed to support the implementation of the new NSW Syllabus, our
            meticulously crafted{' '}
            <Link openInNewTab url="/resources/new-nsw-syllabus/">
              scope and sequence document
            </Link>{' '}
            acts as a blueprint for tailoring your school’s science curriculum while ensuring
            seamless alignment with the NSW curriculum. It is crafted to span a typical school year,
            considering holidays and school events. In true Stile form, this guide is fully
            customisable, empowering you to adapt it to your school’s unique schedule and
            preferences.
          </P>
        </Column>

        <Column>
          <H3>Always up to date and evolving</H3>
          <P>
            We evolve <em>with</em> real-world science, and emerging technologies. This ensures our
            resources stay relevant to students, and interesting for you to teach with. Our
            guarantee to always evolve extends to these syllabus changes, where we’ll create
            NSW-aligned lessons that remain consistently updated.
          </P>
          <P>
            Over the coming months, we’ll rework our teaching and learning resources to
            comprehensively cover the new syllabus. With Stile as your core resource, you’ll have an
            engaging, syllabus-aligned science program ready to use with Years 7 and 9 students in
            2025 and with Years 8 and 10 students in time for implementation in 2026.
          </P>
        </Column>

        <Column>
          <H3>Everything you need</H3>
          <P>
            Our commitment extends to providing educators with everything you need, reflected in our
            diverse range of lessons encompassing practical investigations, depth studies, and a
            strong emphasis on data science – all of which we’ve thoroughly analysed as new NSW
            requirements.{' '}
            <Link openInNewTab url="https://blog.stileeducation.com/the-new-nsw-syllabus/">
              Read more on the five key change areas.
            </Link>{' '}
            We believe the best way for students to learn data science is by engaging with
            real-world data in relevant contexts. Our exciting partnership with Lunaria One
            exemplifies this approach, which you can explore further below.
          </P>
        </Column>
      </TextBlock>

      <TeaserBlock
        title="We’re going to the Moon!"
        variant="dark"
        contentSide="right"
        asideMax="200px"
        contentMin="100px"
        image={
          <StaticImage
            height={450}
            src="../../../../stile-shared/assets/images/nsw/lunaria.png"
            alt=""
          />
        }
      >
        <P>
          Stile has an exclusive partnership with Australian space start-up,{' '}
          <Link openInNewTab variant="inherit" url="/resources/lunaria-one/">
            Lunaria One
          </Link>{' '}
          which aims to boost sustainable activities in space. Through this partnership, your
          students will have the opportunity to partake in their first mission to answer the
          question:{' '}
          <strong>
            <em>Can plants grow on the Moon?</em>
          </strong>
        </P>
        <P>
          Students will explore both the living systems and data science focus areas of the new
          syllabus through our updated Plants unit. In 2026, students will be part of history as the
          Lunaria One project lands actual plant seeds, chosen by students, on the Moon, and follow
          along as we see if they can grow.
        </P>
        <P>
          This research is a key step in determining whether we can grow a food supply in space that
          could sustain a human colony beyond planet Earth!
        </P>
      </TeaserBlock>

      <ResourceAccessBlock
        image={<StaticImage src="../../../../stile-shared/assets/images/nsw/form.png" alt={''} />}
        id="NSW_SYLLABUS_2026"
        redirectUrl="http://stileapp.com/go/nswsyllabus"
      >
        <P>
          Stile is a complete, coherent curriculum for science classrooms in New South Wales. Use
          our Scope and Sequence as a world-class starting point for designing your school’s science
          curriculum. Alongside our helpful teaching plans and lab guides, it offers all the support
          that graduate teachers need, while the ability to customise gives experienced teachers
          flexibility and freedom.
        </P>
      </ResourceAccessBlock>

      <NextPageBlock path="/what-is-stile/stile-classroom/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default NSWSyllabusPage;
